import React, { useEffect, useMemo, useState } from "react";
import { Clock, LogIn, LogOut, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccInput, ccBtnPink, ccBtnBlue, EmptyState } from "../care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (ts) => new Date(ts).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Pair in/out punches per user → shifts with hours
const buildShifts = (punches) => {
  const byUser = {};
  [...punches].sort((a, b) => new Date(a.ts) - new Date(b.ts)).forEach((p) => {
    byUser[p.userEmail] = byUser[p.userEmail] || { name: p.userName, open: null, shifts: [] };
    const u = byUser[p.userEmail];
    if (p.direction === "in") u.open = p;
    else if (u.open) {
      u.shifts.push({ inTs: u.open.ts, outTs: p.ts, hours: Math.round(((new Date(p.ts) - new Date(u.open.ts)) / 3600000) * 100) / 100 });
      u.open = null;
    }
  });
  return byUser;
};

export default function TimeClock({ ctx }) {
  const isAdmin = ctx.role === "admin";
  const [punches, setPunches] = useState(null);
  const [pinSent, setPinSent] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    fetch(`${API}/api/workspace/timeclock`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setPunches)
      .catch((err) => console.error(err));

  useEffect(() => {
    load();
  }, []);

  const myEmail = ctx.currentUserEmail;
  const myLast = (punches || []).find((p) => p.userEmail === myEmail);
  const onClock = myLast?.direction === "in";
  const nextDirection = onClock ? "out" : "in";

  const requestPin = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workspace/punch/pin`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not send PIN");
      setPinSent(true);
      toast.success("Verification PIN sent to your email — expires in 10 minutes.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const punch = async () => {
    if (pin.trim().length !== 4) return toast.error("Enter the 4-digit PIN from your email.");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workspace/punch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction: nextDirection, pin: pin.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Punch failed");
      toast.success(`Punched ${nextDirection} at ${fmtTime(body.ts)}.`);
      setPin("");
      setPinSent(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const shifts = useMemo(() => buildShifts(punches || []), [punches]);

  return (
    <div className="space-y-6" data-testid="timeclock">
      {/* Punch card */}
      <div className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-5 max-w-xl dark:bg-[#273244] dark:border-[#374457]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h4 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#5CB3DC]" /> Staff Time Clock
            </h4>
            <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">
              {onClock ? `You punched in ${fmtTime(myLast.ts)} — currently on the clock.` : "You are off the clock."}
            </p>
          </div>
          <span
            className={`px-3 py-1 border text-[10px] font-extrabold uppercase rounded-full ${onClock ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-[#FCE6E9] border-[#F8C8DC] text-[#B04A6B]"}`}
            data-testid="punch-status"
          >
            {onClock ? "On clock" : "Off clock"}
          </span>
        </div>

        {!pinSent ? (
          <button onClick={requestPin} disabled={busy} className={`${ccBtnBlue} mt-4 disabled:opacity-50`} data-testid="punch-request-pin-btn">
            <Mail className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Email Me a 4-Digit PIN
          </button>
        ) : (
          <div className="flex gap-2 mt-4 flex-wrap items-center">
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              className={`${ccInput} max-w-[130px] text-center text-lg tracking-[0.5em] font-black`}
              data-testid="punch-pin-input"
            />
            <button onClick={punch} disabled={busy} className={`${ccBtnPink} disabled:opacity-50`} data-testid="punch-submit-btn">
              {nextDirection === "in" ? <LogIn className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> : <LogOut className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
              Punch {nextDirection === "in" ? "In" : "Out"}
            </button>
            <button onClick={requestPin} disabled={busy} className="text-[11px] font-bold text-[#5CB3DC] hover:underline" data-testid="punch-resend-pin">
              Resend PIN
            </button>
          </div>
        )}
        <p className="text-[10px] text-[#9CA3AF] font-medium mt-3 flex items-center gap-1">
          <KeyRound className="w-3 h-3" /> Every punch is verified with a single-use PIN emailed to you.
        </p>
      </div>

      {/* Log */}
      {punches === null ? (
        <p className="text-sm text-[#9CA3AF] font-semibold">Loading time log…</p>
      ) : punches.length === 0 ? (
        <EmptyState text="No punches recorded yet." testId="timeclock-empty" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="timeclock-summary">
            {Object.entries(shifts).map(([email, u]) => (
              <div key={email} className="bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-2xl p-4 dark:bg-[#273244]" data-testid={`timeclock-user-${email}`}>
                <p className="text-xs font-black text-[#374151] dark:text-slate-100">{u.name || email}</p>
                <p className="text-[11px] text-[#3D7E9E] font-bold mt-1">
                  {u.shifts.length} shift{u.shifts.length === 1 ? "" : "s"} · {u.shifts.reduce((s, sh) => s + sh.hours, 0).toFixed(2)}h total
                  {u.open ? " · on clock now" : ""}
                </p>
              </div>
            ))}
          </div>
          <div className="space-y-2" data-testid="timeclock-log">
            {punches.slice(0, 30).map((p) => (
              <div key={p.id} className="flex items-center justify-between bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 dark:bg-[#273244] dark:border-[#374457]" data-testid={`punch-row-${p.id}`}>
                <p className="text-xs font-bold text-[#374151] dark:text-slate-100">{p.userName || p.userEmail}</p>
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-0.5 border text-[10px] font-extrabold uppercase rounded-full ${p.direction === "in" ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-[#FCE6E9] border-[#F8C8DC] text-[#B04A6B]"}`}>
                    {p.direction === "in" ? "In" : "Out"}
                  </span>
                  <span className="text-[11px] text-[#9CA3AF] font-medium">{fmtTime(p.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isAdmin && <p className="text-[10px] text-[#9CA3AF] font-medium">Admin view: all staff punches for this facility.</p>}
    </div>
  );
}
